import { useRef, useEffect } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { OBB } from 'three/examples/jsm/math/OBB.js'
import { usePOIHoverStore } from '../stores/usePOIHoverStore'
import { useCameraStore } from '../stores/useCameraStore'

// Highlight visual settings
const HIGHLIGHT_EMISSIVE = new THREE.Color(0.15, 0.2, 0.1)
const HIGHLIGHT_EMISSIVE_INTENSITY = 0.8
const HIGHLIGHT_COLOR_TINT = new THREE.Color(0.95, 1.0, 0.95)

interface MeshEntry {
  mesh: THREE.Mesh
  bbox: THREE.Box3
  name: string
}

interface ActiveHighlight {
  meshIdx: number
  originalMaterial: THREE.Material | THREE.Material[]
  clonedMaterial: THREE.MeshStandardMaterial
}

export function useMeshHighlight() {
  const { scene } = useThree()

  const meshEntriesRef = useRef<MeshEntry[]>([])
  const overlapCacheRef = useRef<Map<string, number[]>>(new Map())
  const activeHighlightsRef = useRef<ActiveHighlight[]>([])
  const prevHoveredRef = useRef<string | null>(null)
  const prevDetectForRef = useRef<string | null>(null)
  const initializedRef = useRef(false)

  // Clear overlap cache when POIs change (collider edits saved)
  const pois = useCameraStore((s) => s.pois)
  useEffect(() => {
    overlapCacheRef.current.clear()
  }, [pois])

  // Clear overlap cache when collider overrides change (live editing)
  const colliderOverrides = usePOIHoverStore((s) => s.colliderOverrides)
  useEffect(() => {
    overlapCacheRef.current.clear()
  }, [colliderOverrides])

  // Build mesh entries once scene materials are applied
  useFrame(() => {
    if (initializedRef.current) return

    const entries: MeshEntry[] = []
    scene.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return
      const mat = child.material as THREE.MeshStandardMaterial
      if (!mat) return

      child.geometry.computeBoundingBox()
      if (!child.geometry.boundingBox) return

      const bbox = new THREE.Box3()
      bbox.copy(child.geometry.boundingBox)
      bbox.applyMatrix4(child.matrixWorld)

      entries.push({
        mesh: child,
        bbox,
        name: child.name || child.uuid,
      })
    })

    if (entries.length > 0) {
      meshEntriesRef.current = entries
      initializedRef.current = true
    }
  })

  // Proactive detection for dev panel (when wireframes shown + POI selected)
  useFrame(() => {
    if (!initializedRef.current) return

    const hoverStore = usePOIHoverStore.getState()
    const detectForId = hoverStore.detectForPOIId

    if (detectForId === prevDetectForRef.current) return
    prevDetectForRef.current = detectForId

    if (!detectForId) return

    // Run detection and expose mesh names
    const allOverlapping = computeRawOverlaps(detectForId, meshEntriesRef.current)
    const meshNames = allOverlapping.map((idx) => meshEntriesRef.current[idx].name)
    hoverStore.setDetectedMeshes(detectForId, meshNames)
  })

  // Reactive highlight application
  useFrame(() => {
    if (!initializedRef.current) return

    const hoveredPOIId = usePOIHoverStore.getState().hoveredPOIId

    if (hoveredPOIId === prevHoveredRef.current) return
    prevHoveredRef.current = hoveredPOIId

    // Restore previously highlighted meshes
    for (const h of activeHighlightsRef.current) {
      const entry = meshEntriesRef.current[h.meshIdx]
      if (entry) {
        entry.mesh.material = h.originalMaterial
      }
      h.clonedMaterial.dispose()
    }
    activeHighlightsRef.current = []

    if (!hoveredPOIId) return

    if (!usePOIHoverStore.getState().materialHighlightEnabled) return

    // Find overlapping meshes (cached, respects exclusions)
    let overlapping = overlapCacheRef.current.get(hoveredPOIId)
    if (!overlapping) {
      overlapping = computeFilteredOverlaps(hoveredPOIId, meshEntriesRef.current)
      overlapCacheRef.current.set(hoveredPOIId, overlapping)

      // Also update detected meshes (all overlaps, not filtered)
      const allOverlapping = computeRawOverlaps(hoveredPOIId, meshEntriesRef.current)
      const meshNames = allOverlapping.map((idx) => meshEntriesRef.current[idx].name)
      usePOIHoverStore.getState().setDetectedMeshes(hoveredPOIId, meshNames)
    }

    // Apply highlights
    for (const idx of overlapping) {
      const entry = meshEntriesRef.current[idx]
      const origMat = entry.mesh.material as THREE.MeshStandardMaterial

      const clone = origMat.clone()
      clone.emissive.copy(HIGHLIGHT_EMISSIVE)
      clone.emissiveIntensity = HIGHLIGHT_EMISSIVE_INTENSITY
      clone.color.copy(origMat.color).multiply(HIGHLIGHT_COLOR_TINT)

      entry.mesh.material = clone

      activeHighlightsRef.current.push({
        meshIdx: idx,
        originalMaterial: origMat,
        clonedMaterial: clone,
      })
    }
  })

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      for (const h of activeHighlightsRef.current) {
        const entry = meshEntriesRef.current[h.meshIdx]
        if (entry) {
          entry.mesh.material = h.originalMaterial
        }
        h.clonedMaterial.dispose()
      }
      activeHighlightsRef.current = []
    }
  }, [])
}

/** Get collider config for a POI (respects live overrides) */
function getCollider(poiId: string) {
  const hoverStore = usePOIHoverStore.getState()
  const pois = useCameraStore.getState().pois
  const poi = pois.find((p) => p.id === poiId)
  if (!poi) return null
  return { poi, collider: hoverStore.getColliderForPOI(poi) }
}

/** Build an OBB for a POI's collider (supports Y-axis rotation) */
function buildColliderOBB(
  poi: { target: { x: number; y: number; z: number } },
  collider: {
    size: { x: number; y: number; z: number }
    offset: { x: number; y: number; z: number }
    rotation?: number
  }
) {
  const center = new THREE.Vector3(
    poi.target.x + collider.offset.x,
    poi.target.y + collider.offset.y,
    poi.target.z + collider.offset.z
  )
  const halfSize = new THREE.Vector3(collider.size.x / 2, collider.size.y / 2, collider.size.z / 2)
  const rotation = new THREE.Matrix3()
  if (collider.rotation) {
    const rad = THREE.MathUtils.degToRad(collider.rotation)
    const cos = Math.cos(rad)
    const sin = Math.sin(rad)
    // Y-axis rotation matrix
    rotation.set(cos, 0, sin, 0, 1, 0, -sin, 0, cos)
  }
  return new OBB(center, halfSize, rotation)
}

/** Raw overlaps: all meshes that intersect collider (no exclusion filtering) */
function computeRawOverlaps(poiId: string, entries: MeshEntry[]): number[] {
  const data = getCollider(poiId)
  if (!data) return []

  const colliderOBB = buildColliderOBB(data.poi, data.collider)

  const result: number[] = []
  for (let i = 0; i < entries.length; i++) {
    if (colliderOBB.intersectsBox3(entries[i].bbox)) {
      result.push(i)
    }
  }
  return result
}

/** Filtered overlaps: raw overlaps minus excluded meshes */
function computeFilteredOverlaps(poiId: string, entries: MeshEntry[]): number[] {
  const data = getCollider(poiId)
  if (!data) return []

  const excluded = new Set(data.collider.excludedMeshes ?? [])
  const raw = computeRawOverlaps(poiId, entries)

  if (excluded.size === 0) return raw
  return raw.filter((idx) => !excluded.has(entries[idx].name))
}
